import { validateEmail } from "@vcl";
import { $axios } from "@bloglovin/vue-framework";
import { defineStore } from "pinia";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { links } from "%/dynamic-component-editor/enums/link-enums";
import {
  ACCOUNT_DELETED_ERROR_CODE,
  EMAIL_FORM,
  TWO_FACTOR_ENROLL_FORM,
  TWO_FACTOR_FORM,
} from "%/dynamic-component-editor/constants/login-flow-constants";
import { resolveEmailCheckOutcome, resolveSignUpDestination } from "%/dynamic-component-editor/helpers/login-flow-steps";
import { createPasskey, getPasskeyAssertion } from "%/utils/webauthn";

export const useLoginSignupStore = defineStore("login-signup-store", {
  state: () => ({
    userAuthEmail: "",
    userFlowSteps: [EMAIL_FORM],
    userFirstName: "",
    userLastName: "",
    lastLoggedInUsers: {},
    isLoading: false,
    loginErrorMessage: "",
    signupErrorMessage: "",
    forgotPasswordErrorMessage: "",
    verifyUserErrorMessage: "",
    signUpNote: "",
    redirect: "",
    guestJoinUrl: "",
    loginWithInspirationRequestId: 0,
    loginWithInspirationRequestToken: "",
    invitationToken: "",
    invitedUserId: 0,
    invitedPublicationId: 0,
    invitedPublicationName: "",
    mfaChallengeToken: "",
    mfaMethods: [],
    passkeyAssertionOptions: null,
    twoFactorErrorMessage: "",
    forcedEnrollSecret: "",
    forcedEnrollOtpauthUri: "",
    forcedEnrollQrDataUri: "",
    forcedEnrollBackupCodes: [],
  }),
  getters: {
    appId() {
      return useGeneralStore().appId;
    },
    hasValidUserAuthEmail() {
      return validateEmail(this.userAuthEmail);
    },
    currentUserFlowStep() {
      return this.userFlowSteps.at(-1);
    },
    previousUserFlowStep() {
      return this.userFlowSteps.at(-2);
    },
    usersApiV1Url() {
      return useGeneralStore().getLink(links.usersApiV1);
    },
    creatorApiV1() {
      return useGeneralStore().getLink(links.api);
    },
    fanApiV1Url() {
      return useGeneralStore().getLink(links.fanApiV1);
    },
    userId() {
      return useGeneralStore().userId;
    },
    userData() {
      return useGeneralStore().userData;
    },
  },
  actions: {
    pushStep(step) {
      this.userFlowSteps.push(step);
    },
    popStep() {
      if (this.userFlowSteps.length > 1) {
        this.userFlowSteps.pop();
      }
    },
    resetToEmailStep() {
      this.userFlowSteps = [EMAIL_FORM];
    },
    parseError(error) {
      const errorData = error?.response?.data ?? {};
      if (errorData.code === ACCOUNT_DELETED_ERROR_CODE && errorData.message) {
        return errorData.message;
      }
      if (errorData.message === "The password you entered is incorrect. Please try again.") {
        return "Incorrect password, please try again.";
      }
      return "Login failed, please try again.";
    },
    async verifyUserExists(email, appId = null) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.verifyUserErrorMessage = "";
      this.signUpNote = "";

      const resolvedAppId = appId ?? this.appId;
      const encodedEmail = encodeURIComponent(email);
      const verifyUserUri = `${this.usersApiV1Url}/user/app/${resolvedAppId}/exists?email=${encodedEmail}`;
      return $axios.get(verifyUserUri).then(response => {
        this.userAuthEmail = email;
        const deleted = response.data.deleted === true;
        const exists = response.data.exists !== false && !deleted;
        if (exists) {
          this.userFirstName = response.data.first_name;
        }
        const { step, signUpNote } = resolveEmailCheckOutcome({
          exists,
          deleted,
          canSignUp: useConfigurationStore().canSignUp,
        });
        this.signUpNote = signUpNote;
        this.pushStep(step);
        return { email, exists, deleted };
      }).catch(error => {
        this.verifyUserErrorMessage = error?.message
          ?? "something went wrong checking to see if user has account";
      }).finally(() => {
        this.isLoading = false;
      });
    },
    loginUser(password, appId = null) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios.post("/user/login", {
        app_id: appId ?? this.appId,
        email: this.userAuthEmail,
        password: password,
        login_with_inspiration_request_id: this.loginWithInspirationRequestId,
        login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
        redirect: this.redirect,
      }).then(response => {
        if (response.data.result === "mfa_required") {
          this.mfaChallengeToken = response.data.challenge_token;
          this.mfaMethods = response.data.methods ?? [];
          this.passkeyAssertionOptions = response.data.passkey_assertion_options ?? null;
          this.twoFactorErrorMessage = "";
          this.pushStep(TWO_FACTOR_FORM);
          return;
        }
        if (response.data.result === "mfa_enrollment_required") {
          this.mfaChallengeToken = response.data.challenge_token;
          this.twoFactorErrorMessage = "";
          this.pushStep(TWO_FACTOR_ENROLL_FORM);
          return;
        }
        if (response.data.success) {
          window.location.href = this.redirect;
          return;
        }
        if (response.data.old_password) {
          this.loginErrorMessage = `You entered an old password, you changed it ${response.data.date_changed}`;
        } else {
          this.loginErrorMessage = "login failed for an unknown reason";
        }
      }).catch(error => {
        this.loginErrorMessage = this.parseError(error);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    completeSecondFactor(endpoint, payload) {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      return $axios.post(endpoint, {
        challenge_token: this.mfaChallengeToken,
        login_with_inspiration_request_id: this.loginWithInspirationRequestId,
        login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
        redirect: this.redirect,
        ...payload,
      }).then(response => {
        if (response.data.success) {
          window.location.href = this.redirect;
          return;
        }
        this.twoFactorErrorMessage = "Verification failed, please try again.";
      }).catch(error => {
        this.twoFactorErrorMessage = this.parseError(error);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    verifyCode(code) {
      return this.completeSecondFactor("/user/login/verify-code", { code });
    },
    async verifyPasskey() {
      if (!this.passkeyAssertionOptions) {
        this.twoFactorErrorMessage = "No passkey is registered for this account.";
        return;
      }
      this.twoFactorErrorMessage = "";
      try {
        const assertion = await getPasskeyAssertion(this.passkeyAssertionOptions);
        return this.completeSecondFactor("/user/login/verify-passkey", assertion);
      } catch (error) {
        this.twoFactorErrorMessage = error?.message ?? "Passkey authentication was cancelled.";
      }
    },
    enrollForcedTotp() {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      return $axios.post("/user/login/enroll-totp", { challenge_token: this.mfaChallengeToken })
          .then(response => {
            this.forcedEnrollSecret = response.data.secret;
            this.forcedEnrollOtpauthUri = response.data.otpauth_uri;
            this.forcedEnrollQrDataUri = response.data.qr_svg_data_uri;
          }).catch(error => {
            this.twoFactorErrorMessage = this.parseError(error);
          }).finally(() => {
            this.isLoading = false;
          });
    },
    confirmForcedTotp(code) {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      return $axios.post("/user/login/confirm-totp", {
        challenge_token: this.mfaChallengeToken,
        login_with_inspiration_request_id: this.loginWithInspirationRequestId,
        login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
        redirect: this.redirect,
        code,
      }).then(response => {
        if (response.data.success) {
          this.forcedEnrollBackupCodes = response.data.backup_codes ?? [];
          if (this.forcedEnrollBackupCodes.length === 0) {
            window.location.href = this.redirect;
          }
          return;
        }
        this.twoFactorErrorMessage = "Verification failed, please try again.";
      }).catch(error => {
        this.twoFactorErrorMessage = this.parseError(error);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    async forcedRegisterPasskey(alias) {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      try {
        const optionsResponse = await $axios.post("/user/login/passkey/register-options", {
          challenge_token: this.mfaChallengeToken,
        });
        const attestation = await createPasskey(optionsResponse.data.public_key);
        const response = await $axios.post("/user/login/passkey/register", {
          challenge_token: this.mfaChallengeToken,
          registration_token: optionsResponse.data.registration_token,
          alias,
          login_with_inspiration_request_id: this.loginWithInspirationRequestId,
          login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
          redirect: this.redirect,
          ...attestation,
        });
        if (response.data.success) {
          this.forcedEnrollBackupCodes = response.data.backup_codes ?? [];
          if (this.forcedEnrollBackupCodes.length === 0) {
            window.location.href = this.redirect;
          }
          return;
        }
        this.twoFactorErrorMessage = "Passkey registration failed, please try again.";
      } catch (error) {
        this.twoFactorErrorMessage = error?.response ? this.parseError(error) : error?.message ?? "Could not register a passkey.";
      } finally {
        this.isLoading = false;
      }
    },
    finishForcedEnroll() {
      window.location.href = this.redirect;
    },
    validateSignupFormData(firstName, lastName, email, password, confirmPassword) {
      if (!firstName.trim()) {
        this.signupErrorMessage = "First name is required";
        return false;
      }
      if (!lastName.trim()) {
        this.signupErrorMessage = "Last name is required";
        return false;
      }
      if (!email.trim()) {
        this.signupErrorMessage = "Email is required";
        return false;
      }
      if (!password.trim()) {
        this.signupErrorMessage = "Password is required";
        return false;
      }
      if (!confirmPassword.trim()) {
        this.signupErrorMessage = "Confirm password is required";
        return false;
      }
      if (password !== confirmPassword) {
        this.signupErrorMessage = "Passwords do not match";
        return false;
      }
      this.signupErrorMessage = "";
      return true;
    },
    signUpUser(firstName, lastName, email, password, appId = null) {
      this.isLoading = true;
      $axios.post("/user/create", {
        org_name: `${firstName} ${lastName}`,
        first_name: firstName,
        last_name: lastName,
        email: email,
        password: password,
        app_id: appId ?? this.appId,
        login_with_inspiration_request_id: this.loginWithInspirationRequestId,
        login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
        redirect: this.redirect,
      }).then(() => {
        window.location.href = resolveSignUpDestination({
          requirePaidSubscription: useConfigurationStore().requirePaidSubscription,
          redirect: this.redirect,
          fanUrl: useGeneralStore().fanUrl,
          hostname: window.location.hostname,
        });
      }).catch(error => {
        this.signupErrorMessage = error?.message
          ?? "something unexpected went wrong during signup";
      }).finally(() => {
        this.isLoading = false;
      });
    },
    signUpAndAcceptInvitation(password) {
      this.apiPost(
        "/user/invitation/publication/accept",
        {
          user_id: this.invitedUserId,
          first_name: this.userFirstName,
          last_name: this.userLastName,
          password: password,
          token: this.invitationToken,
          publication_id: this.invitedPublicationId,
        },
        () => {
          window.location.href = this.redirect;
        },
        error => {
          this.signupErrorMessage = error?.response?.data?.message
            ?? error?.message
            ?? "something unexpected went wrong during registration";
        },
      );
    },
    authenticateLoginWithInspiration() {
      this.isLoading = true;
      $axios.post("/user/login-with-inspiration/authenticate", {
        login_with_inspiration_request_id: this.loginWithInspirationRequestId,
        login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
        redirect: this.redirect,
      }).then(response => {
        window.location.href = response.data.redirect ?? this.redirect;
      }).catch(error => {
        this.signupErrorMessage = this.parseError(error);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    forgotPassword(appId = null) {
      this.isLoading = true;
      $axios.post("/user/forgot-password/initiate", {
        app_id: appId ?? this.appId,
        email: this.userAuthEmail,
        redirect: `${useGeneralStore().links.web}/login`,
      }).catch(error => {
        this.forgotPasswordErrorMessage = error?.message
          ?? "We are unable to reset your password at this time. Please try again later.";
      }).finally(() => {
        this.isLoading = false;
      });
    },
  },
});
